import type { ProspectImporter } from '../../../types/adminRosterUploads';
import { humanize, type StatusTone } from '../roster-uploads/rosterUploadFormat';

/**
 * Display rules for prospect provenance: the "Prospects from this upload" list on an Uploaded
 * Rosters detail and the "Came from" panel on a prospect. Pure, so both views say the same thing.
 */

/** Where a prospect's page lives. The Players list opens its viewer from `?prospectId=`. */
export const prospectHref = (prospectId: string): string =>
  `/players?prospectId=${encodeURIComponent(prospectId)}`;

export const rosterUploadHref = (rosterExtractionId: string): string =>
  `/admin/roster-uploads/${encodeURIComponent(rosterExtractionId)}`;

export const batchExtractionHref = (batchExtractionId: string): string =>
  `/admin/batch-extractions/${encodeURIComponent(batchExtractionId)}/review`;

/**
 * An outcome's label and tone. The word is always rendered, so the tone never carries the meaning
 * on its own.
 */
export const outcomePresentation = (outcome: string): { label: string; tone: StatusTone } => {
  if (outcome === 'Created') return { label: 'Created', tone: 'success' };
  if (outcome === 'MatchedExisting') return { label: 'Matched existing', tone: 'progress' };
  if (!outcome) return { label: 'Unknown', tone: 'neutral' };
  return { label: humanize(outcome), tone: 'neutral' };
};

/** "Last, First", tolerating either half missing. */
export const prospectDisplayName = (firstName: string, lastName: string): string => {
  const first = firstName.trim();
  const last = lastName.trim();
  if (first && last) return `${last}, ${first}`;
  return last || first || 'Unnamed prospect';
};

/** The `sourceKind` of a prospect a coach typed in by hand rather than imported. */
export const MANUAL_ENTRY_SOURCE_KIND = 'ManualEntry';

export const importerLabel = (importedBy: ProspectImporter | null): string =>
  importedBy ? (importedBy.name ?? (importedBy.email || 'Unknown')) : 'Unknown';
